/* TSO powder catalog search — the "Custom Color" searchable dropdown.
   Selecting a Custom Color option (data-colorcustom) in any builder color
   select (main primary/secondary + accessory popups) opens a searchable
   panel over the FULL secondary_color catalog (fetched once from
   /pages/vehicle-data?view=powder-data, localStorage-cached 24h — the
   vehicle-dataset pattern). Picking a powder injects it as a first-class
   <option> carrying its OWN pricing/SKU/fee/lead/hex attrs, then fires a
   native change — the existing pricing, composite-SKU, fee, lead-time and
   canvas-tint (tintAndDraw via data-colorcode) pipelines run untouched.
   "Custom Color" itself stays selectable as the type-a-note fallback. */
(function () {
  'use strict';
  // v2: entries carry the metaobject "id" (Cart Transform GID contract) —
  // bumped so returning visitors don't keep an id-less catalog for 24h
  // v4 (2026-09-21): rows carry `tier`; an older cache would leave every catalog pick at tier 3.
  var CACHE_KEY = 'tso_powder_data_v4';
  var TTL_MS = 24 * 60 * 60 * 1000;
  var DATA_URL = '/pages/vehicle-data?view=powder-data';
  var ROW_LIMIT = 80;
  var SELECTS = '#primaryColorMain, #secondaryColorMain, .accessories-popup-select.primary-colors, .accessories-popup-select.secondary-colors';

  /* catalog "id" may be a bare numeric metaobject id or a full gid string */
  function normalizeGid(id) {
    var s = String(id);
    return s.indexOf('gid://') === 0 ? s : 'gid://shopify/Metaobject/' + s;
  }

  /* The generic "Color Match" entry is the color-match SERVICE, not a powder.
     It was created as a copy of Illusion Red and the storefront render kept
     serving that red swatch image even after the admin field was cleared
     (Ben 2026-08-22) — so never trust an image on it: scrub to the hex.
     Applied on BOTH the cache-read and fresh-fetch paths. */
  var CM_LABEL = 'Color Match';
  function scrubCatalog(colors) {
    (colors || []).forEach(function (c) { if (c && c.label === CM_LABEL) c.image = ''; });
    return colors;
  }

  var dataPromise = null;
  var panel = null;
  var activeSelect = null;

  function fetchCatalog() {
    if (dataPromise) return dataPromise;
    dataPromise = new Promise(function (resolve) {
      try {
        var cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && cached.t && Date.now() - cached.t < TTL_MS && Array.isArray(cached.colors) && cached.colors.length) {
          return resolve(scrubCatalog(cached.colors));
        }
      } catch (e) { /* corrupt cache -> refetch */ }
      // 5,300+ colors = 22 pages; fetching them SEQUENTIALLY took ~10s cold
      // (2026-07-07 Ben report). Page 1 carries the total page count, so
      // fetch the rest in PARALLEL — cold load ≈ one round-trip.
      function getPage(n, pp) {
        return fetch(DATA_URL + '&' + (pp || 'page') + '=' + n).then(function (r) { return r.json(); });
      }
      getPage(1).then(function (first) {
        var pp = first.page_param || 'page';
        var total = Math.max(1, parseInt(first.pages, 10) || (first.has_next ? 2 : 1));
        var rest = [];
        for (var n = 2; n <= total; n++) {
          rest.push(getPage(n, pp).catch(function () { return { colors: [] }; }));
        }
        Promise.all(rest).then(function (pages) {
          var all = (first.colors || []).slice();
          pages.forEach(function (d) { all = all.concat(d.colors || []); });
          // never cache a partial/empty catalog
          if (all.length) { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), colors: all })); } catch (e) { /* quota */ } }
          resolve(scrubCatalog(all));
        });
      }).catch(function () { resolve([]); });
    });
    return dataPromise;
  }

  /* sort: sku_code "-<n>" numeric ascending first (Black -1 before Gloss
     Black -2), everything else alphabetical after (Ben's rule) */
  function numSuffix(sku) {
    var m = /-(\d+)$/.exec(sku || '');
    return m ? parseInt(m[1], 10) : null;
  }
  function sortColors(list) {
    return list.slice().sort(function (a, b) {
      var na = numSuffix(a.sku), nb = numSuffix(b.sku);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      if (nb !== null) return 1;
      return String(a.label || '').localeCompare(String(b.label || ''));
    });
  }

  function matches(c, q) {
    if (!q) return true;
    /* internal sku_code is sort-only (Ben) — search = name + manufacturer SKU */
    return String(c.label || '').toLowerCase().indexOf(q) !== -1
      || String(c.msku || '').toLowerCase().indexOf(q) !== -1;
  }

  function closePanel() {
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
    panel = null;
    activeSelect = null;
  }

  /* ---- notes box is OPT-IN (Ben): the builder's own togglers show
     .customization-wrap whenever Custom Color is selected; we enforce
     visibility AFTER them (setTimeout 0 past the jQuery handlers) so it only
     shows once the panel's "describe it" fallback was clicked in that
     context (main step or one popup) — or when it already holds text
     (saved-build restores). Hidden again when no select in the context has
     Custom Color. Primary + secondary each open their own panel; either can
     opt the shared context notes in. */
  function notesContext(sel) {
    var pp = sel.closest('.accessories-popup-overlay');
    if (pp) {
      return {
        wrap: pp.querySelector('.customization-wrap'),
        selects: pp.querySelectorAll('.accessories-popup-select.primary-colors, .accessories-popup-select.secondary-colors')
      };
    }
    return {
      wrap: document.querySelector('.color-options-step .customization-wrap'),
      selects: document.querySelectorAll('#primaryColorMain, #secondaryColorMain')
    };
  }
  function anyCustomSelected(selects) {
    for (var i = 0; i < selects.length; i++) {
      var o = selects[i].options[selects[i].selectedIndex];
      if (o && o.getAttribute('data-colorcustom') === 'true') return true;
    }
    return false;
  }
  function enforceNotes(sel) {
    var ctx = notesContext(sel);
    if (!ctx.wrap) return;
    var input = ctx.wrap.querySelector('.customization-input, .customization-input-main, textarea, input[type="text"]');
    var hasText = !!(input && input.value && input.value.trim() !== '');
    var show = anyCustomSelected(ctx.selects) && (ctx.wrap.getAttribute('data-tso-notes-optin') === '1' || hasText);
    ctx.wrap.style.display = show ? '' : 'none';
  }
  function optInNotes(sel) {
    var ctx = notesContext(sel);
    if (!ctx.wrap) return;
    ctx.wrap.setAttribute('data-tso-notes-optin', '1');
    ctx.wrap.style.display = '';
    var input = ctx.wrap.querySelector('.customization-input, .customization-input-main, textarea, input[type="text"]');
    if (input) input.focus();
  }

  function openPanel(sel) {
    closePanel();
    activeSelect = sel;
    panel = document.createElement('div');
    panel.className = 'tso-powder';
    panel.innerHTML =
      '<div class="tso-powder__bar">' +
      '<input type="search" class="tso-powder__input" placeholder="Search 5,500+ powder colors — name or code" aria-label="Search powder colors">' +
      '<button type="button" class="tso-powder__close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="tso-powder__list" role="listbox"><div class="tso-powder__note">Loading colors…</div></div>' +
      '<button type="button" class="tso-powder__fallback">Can’t find it? Describe your color in the notes below instead.</button>';
    var block = sel.closest('[data-cpb-field]') || sel.closest('.color-option') || sel.closest('.accessories-popup-option-block') || sel.parentElement;
    block.appendChild(panel);
    panel.querySelector('.tso-powder__close').addEventListener('click', closePanel);
    panel.querySelector('.tso-powder__fallback').addEventListener('click', function () {
      optInNotes(sel);
      closePanel();
    });
    var input = panel.querySelector('.tso-powder__input');
    var listEl = panel.querySelector('.tso-powder__list');
    fetchCatalog().then(function (colors) {
      if (!panel) return;
      var sorted = sortColors(colors);
      function render() {
        var q = input.value.trim().toLowerCase();
        var hits = [];
        for (var i = 0; i < sorted.length && hits.length < ROW_LIMIT; i++) {
          if (matches(sorted[i], q)) hits.push(sorted[i]);
        }
        var total = q ? sorted.filter(function (c) { return matches(c, q); }).length : sorted.length;
        listEl.innerHTML = '';
        hits.forEach(function (c) {
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'tso-powder__row';
          row.setAttribute('role', 'option');
          var swStyle = c.image ? "background-image:url('" + c.image + "');background-size:cover;background-position:center;"
            : 'background:' + (c.hex || '#ccc') + ';';
          /* swatch left, name, manufacturer SKU right when present — the
             internal sort code never shows (Ben) */
          row.innerHTML = '<span class="tso-powder__sw" style="' + swStyle + '"></span>'
            + '<span class="tso-powder__name"></span>'
            + (c.msku ? '<span class="tso-powder__code"></span>' : '');
          row.querySelector('.tso-powder__name').textContent = c.label || '';
          if (c.msku) row.querySelector('.tso-powder__code').textContent = c.msku;
          row.addEventListener('click', function () { pick(sel, c); });
          listEl.appendChild(row);
        });
        if (!hits.length) {
          listEl.innerHTML = '<div class="tso-powder__note">No colors match “' + q.replace(/</g, '&lt;') + '”</div>';
        } else if (total > hits.length) {
          var more = document.createElement('div');
          more.className = 'tso-powder__note';
          more.textContent = 'Showing ' + hits.length + ' of ' + total + ' — keep typing to narrow';
          listEl.appendChild(more);
        }
      }
      var t;
      input.addEventListener('input', function () { clearTimeout(t); t = setTimeout(render, 120); });
      render();
      input.focus();
    });
  }

  /* matchPaint: WHICH truck paint this pick represents. The generic "Color
     Match" sentinel is ONE catalog entry, so on a two-tone truck the paint and
     the trim both resolve to the same label — and label is the identity
     everywhere (pick() dedupes options by it, the tiles key their selected
     state off it, swap moves it between selects). Two different colours
     wearing one name made swap a no-op and lit both tiles at once
     (Ben 2026-08-23). This attribute is what tells them apart; it is display
     identity only and never reaches pricing or the cart. */
  function pick(sel, c, noStripTile, hexOverride, match) {
    var v = String(c.label || '');
    if (!v) return;
    var opt = null;
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === v) { opt = sel.options[i]; break; }
    }
    if (!opt) {
      opt = document.createElement('option');
      opt.value = v;
      sel.appendChild(opt);
    }
    var secOff = parseFloat(sel.getAttribute('data-secpriceoff') || '100');
    opt.textContent = v;
    opt.setAttribute('data-value', v);
    opt.setAttribute('data-primarychange', c.pri == null ? 0 : c.pri);
    opt.setAttribute('data-secondarychange', String((c.sec == null ? 0 : c.sec) * secOff / 100));
    opt.setAttribute('data-skucode', c.sku || '');
    /* hexOverride: the generic "Color Match" sentinel's own colour field is
       #ffffff (verified live 2026-08-22) — it is the color-match SERVICE, not a
       coating, so that white is a placeholder, not the part's finish. A match
       tile hands us the TRUCK PAINT's hex instead and the canvas previews the
       colour the customer will actually get. Tint-only —
       data-colorcode feeds tintAndDraw and the swatch face, never pricing,
       SKU, fee, lead time, GID or uuid (Ben 2026-08-22). */
    var tintHex = hexOverride || c.hex || '#cccccc';
    opt.setAttribute('data-colorcode', tintHex);
    /* set OR clear: the sentinel's <option> is reused for both paints and for
       plain search-panel picks, so a stale paint would outlive its selection.
       The GID rides along so the ORDER can name the exact paint metaobject —
       the line's own colour GID is the powder (or the sentinel), never the
       paint. Stamped for MAPPED picks too: "they colour-matched their truck"
       and "they chose this powder" land on the same label otherwise. */
    if (match && match.paint) opt.setAttribute('data-tso-match-paint', match.paint);
    else opt.removeAttribute('data-tso-match-paint');
    if (match && match.gid) opt.setAttribute('data-tso-match-paint-gid', match.gid);
    else opt.removeAttribute('data-tso-match-paint-gid');
    opt.setAttribute('data-colorcustom', 'false');
    opt.setAttribute('data-polished', 'false');
    opt.setAttribute('data-feeunder500', c.fee ? 'true' : 'false');
    opt.setAttribute('data-leaddays', String(c.lead || 0));
    /* Finish tier (docs/PRICING_TIERS_SPEC.md): the row's own tier when the entry is
       tagged (table colours like Standard Black live in this catalog too); untagged
       catalog powders are tier 3 — same default the Cart Transform applies. */
    opt.setAttribute('data-tier', c.tier == null || c.tier === '' ? '3' : String(c.tier));
    opt.setAttribute('data-termandcond', c.terms ? 'true' : 'false');
    opt.setAttribute('data-tso-powder', '1'); /* marks catalog-injected options — accessory seeding inherits only these */
    /* Cart Transform v17 powder pricing fires ONLY on the metaobject GID line
       property (no label lookup — labels blow the function's 10KB input cap).
       Static colors stay label-only; a stale cached catalog without ids just
       skips the attribute (the payload builders then omit the GID props). */
    if (c.id != null && c.id !== '') opt.setAttribute('data-gid', normalizeGid(c.id));
    /* ERP (Dattum) color id — same catalog entry, so powder options carry it too.
       Absent on a stale cached catalog; the payload builders then omit the prop. */
    if (c.uuid) opt.setAttribute('data-dattumuuid', c.uuid);
    sel.value = v;
    /* update this block's selected-name chip + clear swatch tile selection */
    var block = sel.closest('[data-cpb-field]') || sel.closest('.color-option') || sel.closest('.accessories-popup-option-block');
    if (block) {
      var name = block.querySelector('[data-cpb-selected-name]');
      if (name) name.textContent = v;
      block.querySelectorAll('.cpb-swatch').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      /* the picked powder gets a live swatch in the strip (one dynamic slot
         per block, latest pick wins). data-cpb-index = its option's index, so
         the shared swatch driver handles ring/click/deselect like a static
         tile — the change dispatched below marks it selected.
         noStripTile (match-tile clicks, Ben 2026-08-22): the visible YOUR
         PAINT tile IS the selection — a second injected swatch reads as two
         things selected, so only the old dynamic slot is cleared. */
      var strip = block.querySelector('.cpb-swatches');
      if (strip) {
        var old = strip.querySelector('.cpb-swatch--powder');
        if (old) old.parentNode.removeChild(old);
      }
      if (strip && !noStripTile) {
        var t = document.createElement('button');
        t.type = 'button';
        t.className = 'cpb-swatch cpb-swatch--powder';
        t.setAttribute('data-cpb-index', String(opt.index));
        t.setAttribute('role', 'radio');
        t.setAttribute('aria-checked', 'false');
        t.title = v;
        var face = c.image
          ? "background-image:url('" + c.image + "');background-size:cover;background-position:center;"
          : 'background:' + tintHex + ';'; /* tintHex, not c.hex - see hexOverride above */
        t.innerHTML = '<span class="cpb-swatch__tile" style="' + face + '"></span><span class="cpb-swatch__name"></span>';
        t.querySelector('.cpb-swatch__name').textContent = v;
        strip.insertBefore(t, strip.querySelector('.tso-match-tile')); /* before match tiles; null appends */
      }
    }
    closePanel();
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* open on Custom Color selection anywhere; close when anything else picked.
     enforceNotes runs a tick later so it lands AFTER the builder's own
     jQuery show/hide togglers and wins. */
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || !sel.matches || !sel.matches(SELECTS)) return;
    var opt = sel.options[sel.selectedIndex];
    /* The main builder renders this catalog INLINE under its Custom tab
       (cpb-color-tabs.js), so opening the modal there would throw away the
       canvas and both colour chips the shopper is comparing against. The
       accessory popup has no inline catalog and still gets the panel. */
    var inlineHost = sel.closest && sel.closest('.cpb-field');
    var hasInline = inlineHost && inlineHost.querySelector('.cpb-tab[data-cpb-all]');
    if (opt && opt.getAttribute('data-colorcustom') === 'true' && !hasInline) openPanel(sel);
    else if (activeSelect === sel) closePanel();
    setTimeout(function () { enforceNotes(sel); }, 0);
  }, true);

  /* ---- phase 2: paint color-match tiles ----
     One "// YOUR PAINT" / "// YOUR TRIM" tile per color row for the saved
     vehicle's ANSWERED paints (never for N/A or unanswered). The customer
     sees the same tile whether or not a real powder is mapped in admin
     (Ben 2026-08-22) — only the plumbing differs:
     - MAPPED (cookie paint_color_match carries a real powder label; the
       drawer never writes the generic "Color Match" sentinel): swatch =
       that powder's catalog image, click = pick() of that powder,
       priced as that powder.
     - UNMAPPED (no match key): swatch = the truck paint's own hex
       (<k>_hex, cookie 2026-08-22), click = pick() of the CATALOG's
       generic "Color Match" entry (its own pricing/fee/lead attrs — the
       color-match service; no product palette carries it).
     Tile name shows the PAINT name (e.g. "Summit White") in both cases.
     The click passes noStripTile — the tile itself takes the selected
     ring/tick (synced off the select's change event, so picking any
     other swatch un-marks it); no dynamic strip swatch is injected
     (Ben 2026-08-22: two selected-looking tiles is confusing).
     Legacy cookies carrying the literal "Color Match" match label are
     treated as unmapped. Tile skipped when its click would do nothing:
     no Custom Color on the select, or nothing pickable in the catalog. */
  function getVehicleSelection() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)vehicle_selection=([^;]+)/);
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }
  function tileDefs(v) {
    var defs = [];
    if (!v) return defs;
    [['paint_color', '// YOUR PAINT'], ['two_tone_paint_color', '// YOUR TRIM']].forEach(function (pair) {
      var name = v[pair[0]];
      if (!name || name === 'N/A') return;
      var match = v[pair[0] + '_match'];
      if (match === CM_LABEL) match = null; /* legacy cookie sentinel */
      defs.push({ eyebrow: pair[1], paint: name, match: match || null,
                  hex: v[pair[0] + '_hex'] || null, gid: v[pair[0] + '_gid'] || null });
    });
    return defs;
  }
  function renderMatchTiles() {
    Array.prototype.slice.call(document.querySelectorAll('.tso-match-tile')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    var defs = tileDefs(getVehicleSelection());
    if (!defs.length) return;
    fetchCatalog().then(function (colors) {
      var byLabel = {};
      (colors || []).forEach(function (c) { byLabel[c.label] = c; });
      var cmEntry = byLabel[CM_LABEL] || null;
      defs.forEach(function (d) { d.entry = d.match ? (byLabel[d.match] || null) : null; });
      Array.prototype.slice.call(document.querySelectorAll(SELECTS)).forEach(function (sel) {
        var hasCustom = false;
        for (var i = 0; i < sel.options.length; i++) {
          if (sel.options[i].getAttribute('data-colorcustom') === 'true') { hasCustom = true; break; }
        }
        if (!hasCustom) return;
        var block = sel.closest('[data-cpb-field]') || sel.closest('.color-option') || sel.closest('.accessories-popup-option-block');
        /* In the tabbed builder these tiles belong to the Color Match TAB and
           nowhere else — appended to the shared strip they showed up under
           Base and Illusion too (Ben 2026-08-22). Everywhere else (accessory
           popup, unmigrated products) the strip is still the right home. */
        var strip = (block && block.querySelector('[data-cpb-match]')) ||
                    (block && block.querySelector('.cpb-swatches'));
        if (!strip) return;
        defs.forEach(function (d) {
          var target = d.entry || cmEntry;
          if (!target) return; /* click would do nothing */
          var tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'tso-match-tile';
          tile.title = d.entry ? 'Match ' + d.paint + ' — ' + d.entry.label : 'Color match ' + d.paint;
          tile.setAttribute('data-tso-match-label', target.label);
          tile.setAttribute('data-tso-match-paint', d.paint);
          if (d.gid) tile.setAttribute('data-tso-match-paint-gid', d.gid);
          tile.setAttribute('aria-pressed', 'false');
          var swStyle = d.entry && d.entry.image
            ? "background-image:url('" + d.entry.image + "');background-size:cover;background-position:center;"
            : 'background:' + ((d.entry && d.entry.hex) || d.hex || '#ccc') + ';';
          /* Show BOTH names when a powder is mapped: the shopper's paint
             ("Star White Tricoat") so they recognise their own truck, and the
             powder it resolves to ("Pearl White") because they sometimes need
             to know the actual coating (Ben 2026-08-22). */
          /* In the tabbed builder these are the ONLY tiles in the Color Match
             tab, so they have to read like every other swatch: price and lead
             time included, same class names so they inherit the grid styling
             (Ben 2026-08-22). Show the shopper's paint AND the powder it
             resolves to — they sometimes need the coating name. */
          var pctBase = 0;
          if (sel.options[0]) pctBase = parseFloat(sel.options[0].getAttribute('data-baseprice') || '0');
          /* Secondary is discounted by the block's data-secpriceoff (50 = half
             price) — pick() bakes that into data-secondarychange, so the tile
             MUST apply it too or it advertises double what it charges
             (14% = $196 shown vs 7% = $98 billed on a $1,399.95 base). */
          var pct;
          if (sel.id === 'secondaryColorMain') {
            var secOff = parseFloat(sel.getAttribute('data-secpriceoff') || '100');
            pct = (target.sec || 0) * secOff / 100;
          } else {
            pct = target.pri || 0;
          }
          var amount = Math.round(pct * pctBase / 100);
          var moneyTxt = (window.Shopify && Shopify.formatMoney)
            ? Shopify.formatMoney(amount) : '$' + (amount / 100).toFixed(2);

          tile.innerHTML = '<span class="tso-match-tile__tile" style="' + swStyle + '"></span>'
            + '<span class="tso-match-tile__label"></span>'
            + '<span class="tso-match-tile__name"></span>'
            + '<span class="tso-match-tile__powder"></span>'
            + '<span class="cpb-swatch__price">' + (amount > 0 ? '+' + moneyTxt : 'No charge') + '</span>'
            + (target.lead ? '<span class="cpb-swatch__lead">+' + target.lead + ' days</span>' : '');
          tile.querySelector('.tso-match-tile__label').textContent = d.eyebrow;
          tile.querySelector('.tso-match-tile__name').textContent = d.paint;
          tile.querySelector('.tso-match-tile__powder').textContent = d.entry ? d.entry.label : '';
          /* unmapped: pick the sentinel but tint with the paint's own hex. The
             paint identity is stamped either way — it keeps a two-tone truck's
             two tiles distinguishable behind their shared "Color Match" label,
             and it is what puts the paint on the order line. */
          var tintHex = d.entry ? null : d.hex;
          var tileMatch = { paint: d.paint, gid: d.gid };
          tile.addEventListener('click', function () { pick(sel, target, true, tintHex, tileMatch); });
          strip.appendChild(tile);
          syncMatchTileState(sel);
        });
      });
    });
  }
  /* match-tile selected state: ON iff the select's current option is the one
     this tile picks (by data-value label). Runs on every change of a color
     select — picking any other swatch (or the search panel) un-marks it. */
  function syncMatchTileState(sel) {
    var block = sel.closest('[data-cpb-field]') || sel.closest('.color-option') || sel.closest('.accessories-popup-option-block');
    if (!block) return;
    var opt = sel.options[sel.selectedIndex];
    var val = opt ? (opt.getAttribute('data-value') || opt.value) : null;
    var optPaint = opt ? opt.getAttribute('data-tso-match-paint') : null;
    Array.prototype.slice.call(block.querySelectorAll('.tso-match-tile')).forEach(function (t) {
      /* Mapped tiles own a unique powder label, so the label alone settles it.
         Sentinel tiles share ONE label across every unmapped paint — there the
         stamped paint is the only thing that separates YOUR PAINT from YOUR
         TRIM, and without it both tiles read as selected (Ben 2026-08-23). */
      var on = !!val && t.getAttribute('data-tso-match-label') === val
        && (val !== CM_LABEL || t.getAttribute('data-tso-match-paint') === optPaint);
      t.classList.toggle('is-selected', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (sel && sel.matches && sel.matches(SELECTS)) syncMatchTileState(sel);
  }, true);
  document.addEventListener('vehicle:selection:updated', renderMatchTiles);
  /* Variant moved → the pct-of-base tile prices are stale. The builder fires
     this AFTER rewriting every option's data-baseprice, so a full re-render
     recomputes from the selected variant (cpb-color-tabs.js reprices its own
     static/catalog tiles off the same event). */
  document.addEventListener('cpb:variant:changed', renderMatchTiles);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderMatchTiles);
  else renderMatchTiles();

  /* the builder's accessory seeding inherits the main build's picked powder:
     inject it into a popup select that lacks it. Gated on the target select
     offering Custom Color — accessories without it keep their own palette. */
  window.TSOPowder = {
    /* Phase-2 colour tabs render the catalog INLINE (cpb-color-tabs.js) instead
       of opening this panel over the builder — the preview has to stay on
       screen while you browse. Both entry points share one cache and one
       pick(), so pricing/SKU/fee/lead/GID/uuid stay identical either way. */
    catalog: function () { return fetchCatalog(); },
    pick: function (sel, color, noStripTile, hexOverride, match) { return pick(sel, color, noStripTile, hexOverride, match); },
    sortColors: function (list) { return sortColors(list); },
    /* hexOverride carries the SOURCE option's data-colorcode so an inherited
       "Color Match" keeps the truck-paint tint instead of falling back to the
       sentinel's placeholder white — the label lookup below can't tell which
       paint it was. */
    injectByLabel: function (sel, label, hexOverride, match) {
      if (!sel || !label) return;
      var hasCustom = false;
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].getAttribute('data-colorcustom') === 'true') { hasCustom = true; break; }
      }
      if (!hasCustom) return;
      fetchCatalog().then(function (colors) {
        for (var i = 0; i < colors.length; i++) {
          if (colors[i].label === label) { pick(sel, colors[i], false, hexOverride, match); return; }
        }
      });
    }
  };

  // Warm the catalog cache in the background on builder pages so the first
  // "Custom Color" open is instant instead of paying the cold fetch.
  if (document.querySelector(SELECTS)) {
    if ('requestIdleCallback' in window) requestIdleCallback(function () { fetchCatalog(); }, { timeout: 5000 });
    else setTimeout(fetchCatalog, 3000);
  }
})();
